import { useCallback, useEffect, useRef, useState } from 'react'

import { fetchMatterArtifactMarkdown } from '@/matter-artifacts/fetch/get-matter-artifact-version-viewable-url'
import { getMatterArtifactVersion } from '@/matter-artifacts/fetch/get-matter-artifact-version'
import { MatterArtifactBaseVersionConflictError, postCreateMatterArtifactVersion } from '@/matter-artifacts/fetch/post-create-matter-artifact-version'
import type { MatterArtifactVersionRead } from '@/matter-artifacts/schemas'
import { EditorSaveStatusType } from '../types'

const kAutoSaveDelayMilliseconds = 750

type UseAgentV2ArtifactEditorProps = {
  matterArtifactId: string
  currentMatterArtifactVersionId: string
  onSavedVersion: (matterArtifactVersion: MatterArtifactVersionRead) => void
  /**
   * Called only when this editor persists a new version of the artifact's content. Distinct from
   * `onSavedVersion`, which also fires while reconciling a conflicting version created elsewhere.
   * Reports the version that now holds the content, which a save creates rather than updates.
   */
  onContentSaved?: (saved: { matterArtifactVersionId: string; contentLength: number }) => void
}

export function useAgentV2ArtifactEditor({ matterArtifactId, currentMatterArtifactVersionId, onSavedVersion, onContentSaved }: UseAgentV2ArtifactEditorProps) {
  const [markdown, setMarkdown] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasLoadError, setHasLoadError] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [saveStatus, setSaveStatus] = useState(EditorSaveStatusType.IDLE)
  const [activeMatterArtifactVersionId, setActiveMatterArtifactVersionId] = useState(currentMatterArtifactVersionId)

  const generationRef = useRef(0)
  const activeSaveRef = useRef<{ generation: number; promise: Promise<boolean> } | null>(null)
  const loadedVersionIdRef = useRef<string | null>(null)
  const currentContentRef = useRef<string | null>(null)
  const lastSavedContentRef = useRef<string | null>(null)
  const conflictRef = useRef<{ currentMatterArtifactVersionId: string | null } | null>(null)
  const onSavedVersionRef = useRef(onSavedVersion)
  const onContentSavedRef = useRef(onContentSaved)

  useEffect(() => {
    onSavedVersionRef.current = onSavedVersion
  }, [onSavedVersion])

  useEffect(() => {
    onContentSavedRef.current = onContentSaved
  }, [onContentSaved])

  useEffect(() => {
    const generation = generationRef.current + 1
    generationRef.current = generation
    activeSaveRef.current = null
    loadedVersionIdRef.current = null
    currentContentRef.current = null
    lastSavedContentRef.current = null
    conflictRef.current = null
    setMarkdown(null)
    setIsLoading(true)
    setHasLoadError(false)
    setSaveStatus(EditorSaveStatusType.IDLE)

    return () => {
      if (generationRef.current === generation) generationRef.current += 1
    }
  }, [matterArtifactId])

  useEffect(() => {
    const generation = generationRef.current
    const isInitialLoad = loadedVersionIdRef.current === null
    let cancelled = false

    if (loadedVersionIdRef.current === currentMatterArtifactVersionId) {
      setHasLoadError(false)
      setIsLoading(false)
      return
    }

    const hasUnsavedContent = currentContentRef.current !== null && currentContentRef.current !== lastSavedContentRef.current
    if (!isInitialLoad && (hasUnsavedContent || activeSaveRef.current?.generation === generation || conflictRef.current)) return

    if (isInitialLoad) {
      setIsLoading(true)
      setHasLoadError(false)
    }

    void fetchMatterArtifactMarkdown(currentMatterArtifactVersionId)
      .then((fetchedMarkdown) => {
        if (cancelled || generationRef.current !== generation) return

        const becameDirty = currentContentRef.current !== null && currentContentRef.current !== lastSavedContentRef.current
        if (!isInitialLoad && (becameDirty || activeSaveRef.current?.generation === generation || conflictRef.current)) return

        loadedVersionIdRef.current = currentMatterArtifactVersionId
        currentContentRef.current = fetchedMarkdown
        lastSavedContentRef.current = fetchedMarkdown
        conflictRef.current = null
        setActiveMatterArtifactVersionId(currentMatterArtifactVersionId)
        setMarkdown(fetchedMarkdown)
        setHasLoadError(false)
        setIsLoading(false)
        setSaveStatus(isInitialLoad ? EditorSaveStatusType.IDLE : EditorSaveStatusType.SAVED)
      })
      .catch(() => {
        if (cancelled || generationRef.current !== generation) return

        setHasLoadError(true)
        setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [currentMatterArtifactVersionId, loadAttempt, matterArtifactId])

  const saveLatestContent = useCallback((): Promise<boolean> => {
    const generation = generationRef.current
    const activeSave = activeSaveRef.current
    if (activeSave?.generation === generation) return activeSave.promise
    if (loadedVersionIdRef.current === null || currentContentRef.current === null || conflictRef.current) return Promise.resolve(false)

    const savePromise = Promise.resolve()
      .then(async () => {
        while (generationRef.current === generation) {
          const contentToSave = currentContentRef.current
          const baseMatterArtifactVersionId = loadedVersionIdRef.current
          if (contentToSave === null || contentToSave === lastSavedContentRef.current) break
          if (!baseMatterArtifactVersionId) return false

          setSaveStatus(EditorSaveStatusType.SAVING)

          let savedVersion: MatterArtifactVersionRead
          try {
            savedVersion = await postCreateMatterArtifactVersion(baseMatterArtifactVersionId, contentToSave)
          } catch (error) {
            if (generationRef.current === generation) {
              if (error instanceof MatterArtifactBaseVersionConflictError) {
                conflictRef.current = {
                  currentMatterArtifactVersionId: error.currentMatterArtifactVersionId,
                }
                setSaveStatus(EditorSaveStatusType.CONFLICT)

                if (error.currentMatterArtifactVersionId) {
                  void getMatterArtifactVersion(error.currentMatterArtifactVersionId)
                    .then((currentVersion) => {
                      if (generationRef.current === generation && conflictRef.current) {
                        onSavedVersionRef.current(currentVersion)
                      }
                    })
                    .catch(() => {})
                }
              } else {
                setSaveStatus(EditorSaveStatusType.ERROR)
              }
            }
            return false
          }

          if (generationRef.current !== generation) return false

          lastSavedContentRef.current = contentToSave
          loadedVersionIdRef.current = savedVersion.id
          setActiveMatterArtifactVersionId(savedVersion.id)
          onSavedVersionRef.current(savedVersion)
          onContentSavedRef.current?.({ matterArtifactVersionId: savedVersion.id, contentLength: contentToSave.length })
        }

        if (generationRef.current !== generation) return false
        setSaveStatus(EditorSaveStatusType.SAVED)
        return currentContentRef.current === lastSavedContentRef.current
      })
      .finally(() => {
        if (activeSaveRef.current?.promise === savePromise) {
          activeSaveRef.current = null
        }
      })

    activeSaveRef.current = { generation, promise: savePromise }
    return savePromise
  }, [])

  const resolveConflict = useCallback(async (): Promise<boolean> => {
    const generation = generationRef.current
    const currentMatterArtifactVersionId = conflictRef.current?.currentMatterArtifactVersionId
    if (!currentMatterArtifactVersionId) return false

    try {
      const [currentVersion, currentMarkdown] = await Promise.all([
        getMatterArtifactVersion(currentMatterArtifactVersionId),
        fetchMatterArtifactMarkdown(currentMatterArtifactVersionId),
      ])
      if (generationRef.current !== generation || !conflictRef.current) return false

      loadedVersionIdRef.current = currentMatterArtifactVersionId
      currentContentRef.current = currentMarkdown
      lastSavedContentRef.current = currentMarkdown
      conflictRef.current = null
      setActiveMatterArtifactVersionId(currentMatterArtifactVersionId)
      setMarkdown(currentMarkdown)
      setSaveStatus(EditorSaveStatusType.SAVED)
      onSavedVersionRef.current(currentVersion)
      return true
    } catch {
      if (generationRef.current === generation) setSaveStatus(EditorSaveStatusType.CONFLICT)
      return false
    }
  }, [])

  useEffect(() => {
    if (markdown === null || markdown === lastSavedContentRef.current) return

    const generation = generationRef.current
    const timeout = window.setTimeout(() => {
      if (generationRef.current === generation) void saveLatestContent()
    }, kAutoSaveDelayMilliseconds)

    return () => window.clearTimeout(timeout)
  }, [markdown, saveLatestContent])

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const hasUnsavedContent =
        currentContentRef.current !== null &&
        (currentContentRef.current !== lastSavedContentRef.current || activeSaveRef.current?.generation === generationRef.current)
      if (!hasUnsavedContent) return

      event.preventDefault()
      event.returnValue = true
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  const handleMarkdownChange = useCallback((nextMarkdown: string) => {
    currentContentRef.current = nextMarkdown
    setMarkdown(nextMarkdown)

    if (conflictRef.current) {
      setSaveStatus(EditorSaveStatusType.CONFLICT)
    } else if (activeSaveRef.current?.generation === generationRef.current) {
      setSaveStatus(EditorSaveStatusType.SAVING)
    } else {
      setSaveStatus(nextMarkdown === lastSavedContentRef.current ? EditorSaveStatusType.SAVED : EditorSaveStatusType.DIRTY)
    }
  }, [])

  const retryLoad = useCallback(() => {
    setLoadAttempt((currentAttempt) => currentAttempt + 1)
  }, [])

  const getActiveMatterArtifactVersionId = useCallback(() => loadedVersionIdRef.current, [])

  return {
    markdown,
    isLoading,
    hasLoadError,
    saveStatus,
    activeMatterArtifactVersionId,
    handleMarkdownChange,
    handleBlur: saveLatestContent,
    saveLatestContent,
    retryLoad,
    retrySave: saveLatestContent,
    resolveConflict,
    getActiveMatterArtifactVersionId,
  }
}
